import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, PackagePlus, ShoppingCart, Sparkles } from 'lucide-react';
import { crossSellAPI, cartAPI, uploadsAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { prodName } from '../services/catalogI18n';
import { useT } from '../services/i18n';

const fmt = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`;

// One recommended complementary product (photo, name, price TTC, verified-compatibility hint, add button)
export const CrossSellCard = ({ item, placement, onAdded, compact = false }) => {
  const { t, lang } = useT();
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState(false);
  const add = async () => {
    setAdding(true);
    try {
      await cartAPI.addItem(item.id, 1);
      window.dispatchEvent(new Event('cart-updated'));
      crossSellAPI.event({ type: 'add', placement, product_id: item.id, source_product_id: item.source_product_id, rule_id: item.rule_id });
      setDone(true); onAdded?.(item);
    } catch (e) { /* toast déjà affiché par cartAPI */ } finally { setAdding(false); }
  };
  const click = () => crossSellAPI.event({ type: 'click', placement, product_id: item.id, source_product_id: item.source_product_id, rule_id: item.rule_id });
  return (
    <div className={`flex ${compact ? 'items-center gap-3 p-2' : 'flex-col p-3'} bg-white border border-gray-100 rounded-xl hover:border-orange-200 hover:shadow-sm transition-all`} data-testid={`cross-sell-item-${item.id}`}>
      <Link to={`/produit/${item.slug}`} onClick={click} className={`${compact ? 'w-16 h-16' : 'w-full aspect-square mb-2'} flex-shrink-0 rounded-lg bg-gray-50 overflow-hidden flex items-center justify-center`}>
        {item.image ? <img src={uploadsAPI.resolveUrl(item.image)} alt="" className="w-full h-full object-contain mix-blend-multiply" loading="lazy" /> : <PackagePlus className="text-gray-300" size={28} />}
      </Link>
      <div className="flex-1 min-w-0">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-orange-700 bg-orange-50 rounded-full px-1.5 py-0.5" data-testid="cross-sell-label"><Sparkles size={9} /> {item.label || t('xsell.badge')}</span>
        <Link to={`/produit/${item.slug}`} onClick={click} className="block text-sm font-medium text-gray-900 leading-snug mt-1 line-clamp-2 hover:text-orange-600">{prodName(item, lang)}</Link>
        <div className="flex items-center justify-between gap-2 mt-1">
          <span className="text-sm font-bold text-gray-900" data-testid="cross-sell-price">{fmt(item.price)} <span className="text-[10px] font-normal text-gray-500">TTC</span></span>
          {item.compatible && <span className="text-[10px] text-green-700 inline-flex items-center gap-0.5" data-testid="cross-sell-compatible"><CheckCircle2 size={10} /> {t('xsell.compatible')}</span>}
        </div>
        {item.specs && <p className="text-[10px] text-gray-500 truncate" title={item.specs}>{item.specs}</p>}
        <button type="button" onClick={add} disabled={adding || done} className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg py-1.5 transition-colors ${done ? 'bg-green-50 text-green-700' : 'bg-gray-900 text-white hover:bg-orange-600'}`} data-testid={`cross-sell-add-${item.id}`}>
          {adding ? <Loader2 size={13} className="animate-spin" /> : done ? <CheckCircle2 size={13} /> : <ShoppingCart size={13} />} {done ? t('product.added') : t('common.addToCart')}
        </button>
      </div>
    </div>
  );
};

// Fetches and renders complementary products for one or several source products (placement: added | cart | product)
const CrossSellSection = ({ placement, productIds, cartIds = [], title, subtitle, compact = false, onAdded, className = '', testId = 'cross-sell', gridClass }) => {
  const vehicle = useSelectedVehicle();
  const [items, setItems] = useState(null);
  const tracked = useRef('');
  const key = JSON.stringify([productIds, cartIds, vehicle?.brand, vehicle?.model, vehicle?.engine?.name]);
  useEffect(() => {
    if (!productIds?.length) { setItems([]); return undefined; }
    let cancelled = false;
    crossSellAPI.recommend({ product_ids: productIds, placement, cart_ids: cartIds, vehicle: vehicleStore.toParams(vehicle) }).then((r) => {
      if (cancelled) return;
      setItems(r.items || []);
      const sig = `${placement}:${(r.items || []).map(i => i.id).join(',')}`;
      if (r.items?.length && tracked.current !== sig) { tracked.current = sig; crossSellAPI.event({ type: 'impression', placement, source_product_id: productIds[0], count: r.items.length }); }
    }).catch(() => !cancelled && setItems([]));
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items?.length) return null;
  return (
    <section className={className} data-testid={testId}>
      {title && <h2 className="text-base font-bold text-gray-900 flex items-center gap-2"><Sparkles size={16} className="text-orange-500" /> {title}</h2>}
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      <div className={gridClass || (compact ? 'grid gap-2 sm:grid-cols-2' : 'grid gap-3 grid-cols-2 md:grid-cols-4')}>
        {items.map(it => <CrossSellCard key={it.id} item={it} placement={placement} compact={compact} onAdded={onAdded} />)}
      </div>
    </section>
  );
};

export default CrossSellSection;
